import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Modal,
  Select,
  Spin,
  message,
} from '@surecompanies/core_components'
import dayjs from 'dayjs'

import { getApplicationDetailsPath } from 'modules/qle/utils/qleCommonLinks'

import { getShortText } from '../PaymentAssignment/Utils/payment-assignment.helper'

import BenefitSelect, { BenefitOption } from './BenefitSelect'
import { colors, space } from './tokens'
import styles from './AdjustPaymentModal.module.scss'
import {
  ADJUST_MODAL_WIDTH,
  EPSILON,
  isReturnableSource,
  RETURN_NOTE_MAX_LENGTH,
  SOURCE_ABLE_TO_DELETE,
  SOURCE_LABEL,
} from './AdjustPaymentModal.constants'
import {
  ChevronDownIcon,
  CopyIcon,
  ExternalLinkIcon,
  FlagIcon,
  PlusIcon,
  SplitIcon,
  TrashIcon,
  UnlinkIcon,
} from './AdjustPaymentModal.icons'
import { replaceOwnedQuery } from './replaceOwnedQuery'
import { useCanWriteReconciliation } from './useCanWriteReconciliation'
import {
  TD_DEEP_LINK_URL_KEYS,
  TD_Q,
} from '../TransactionDetails/hooks/useTransactionDetails'
import {
  absorptionSuccessMessage,
  allocationSignature,
  allocationSuccessMessage,
  bareFmt,
  emptyManualForm,
  extractApiErrorMessage,
  fmtMoney,
  isManualRefundRailValid,
  isMoneyInput,
  isValidCheckNumber,
  manualRefundCheckNumber,
  sourceTxLabels,
  sumRows,
} from './AdjustPaymentModal.helpers'
import {
  AllocationGroup,
  BenefitYearStrip,
  MonthChipGrid,
} from './AdjustPaymentModal.components'
import {
  ExplicitCreditSection,
  useExplicitCreditForm,
} from './ExplicitCreditSection'
import {
  AdjustPaymentTarget,
  AdjustSectionKey,
  GroupState,
  ManualFormState,
} from './AdjustPaymentModal.types'
import {
  ABSORPTION_NOTE_MAX_LENGTH,
  benefitNameLabel,
  buildSetExpectedRequest,
  computePeriodMoneySummary,
  isAbsorptionNoteValid,
  isRefundReason,
  normalizeExpectedAmount,
  OPEN_IN_APP_ENGINE_LABEL,
  REFUND_REASONS,
  REFUND_REASON_LABELS,
  RefundReason,
  resolveSliceClassification,
  splitEvenlyRows,
} from './recon-v2-helpers'

import {
  AbsorptionType,
  AllocateBatchItem,
  AllocationClassification,
  ManualRefundSource,
  useAllocateBatch,
  useDeallocate,
  useRecordAbsorption,
  useDeleteManualPayment,
  useReturnTransaction,
  useEePaid,
  useFlagIssue,
  useGetPeriodDetail,
  useRecordManualRefund,
  useResolveIssue,
  useResolveTransactionBenefit,
  useSetExpectedPrice,
  useSuggestPeriods,
  useUnflagIssue,
} from '../../api/reconciliation-v2-api-client'

export type {
  AdjustPaymentTarget,
  AdjustPaymentCellTarget,
  AdjustPaymentUnassignedTarget,
  AdjustPaymentManualTarget,
} from './AdjustPaymentModal.types'

interface Props {
  open: boolean
  onClose: () => void
  target: AdjustPaymentTarget | null
}

const AdjustPaymentModal: React.FC<Props> = ({ open, onClose, target }) => {
  // Narrow the target once, by construction — downstream code routes through
  // these consts so TS keeps the narrowing without any assertions.
  const cellTarget =
    target && target.kind !== 'unassigned' && target.kind !== 'manual'
      ? target
      : null
  const unassigned = target && target.kind === 'unassigned' ? target : null
  const isManual = target?.kind === 'manual'

  // Write gate — mirrors the EPS AbilityGuard(Write, PaymentReconciliation) on
  // every recon-v2 mutation. Read-only users still see the detail view; only the
  // action buttons below are hidden. Server remains the real boundary.
  const { allowed: canWrite } = useCanWriteReconciliation()

  // Deep-link the active transaction to the Transaction Details tab: switch the
  // recon-v2 tab (ReconciliationV2 listens to `tab=`), seed the ledger search
  // with the transaction id so it filters to that row, and flag it to auto-open
  // its detail modal. Also pin the ledger's year to the transaction's own year
  // — that ledger defaults to the current calendar year, so a prior-year txn
  // would otherwise be filtered out and neither surface nor auto-open. Clear
  // every other td* filter key so a prior payment-type / month / client / recon
  // / split selection cannot hide the target. Then close this modal so the
  // jump is unobstructed.
  const openInTransactionDetails = (
    transactionId: string,
    transactionDate: string | null
  ) => {
    const next: Record<string, string> = {
      tab: 'transactions',
      [TD_Q.q]: transactionId,
      [TD_Q.open]: transactionId,
    }
    const year = transactionDate ? dayjs(transactionDate).year() : NaN
    if (Number.isFinite(year)) next[TD_Q.year] = String(year)
    replaceOwnedQuery([...TD_DEEP_LINK_URL_KEYS], next)
    onClose()
  }

  const {
    data: detail,
    isLoading,
    isError: isPeriodDetailError,
  } = useGetPeriodDetail(
    cellTarget?.benefitId ?? undefined,
    cellTarget?.periodStartDate ?? undefined,
    {
      enabled: open && !!cellTarget?.benefitId && !!cellTarget?.periodStartDate,
    }
  )

  // Cell mode manages the cell per PARENT transaction: every transaction
  // allocated to this benefit-month, each with its full signed total and every
  // live slice. The operator picks one row; the "Allocate to" section edits
  // THAT parent's full allocation state, so re-adjusting always starts from
  // the original transaction, never from a prior split slice.
  const transactions = cellTarget ? (detail?.transactions ?? []) : []
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null)
  // Optional note attached to a cell/unassigned adjustment (distinct from the
  // manual "Add Payment" note, which lives on manualForm).
  const [adjustNote, setAdjustNote] = useState('')

  // The "Absorb" accordion's own note (textarea A), serving BOTH of that
  // section's submits — the full write-off and the overpaid-cell write-off.
  // Deliberately NOT `adjustNote`: that one is an ALLOCATION note living in a
  // different (often collapsed) section, so reusing it would have the operator
  // typing in one place and clicking in another, and the mandatory-note rule
  // could not gate the Absorb button. An absorption is never updated, so this
  // is restated on every submit rather than prefilled (ADR 0003).
  const [absorbNote, setAbsorbNote] = useState('')
  // Required note for the Return section (iStream, unassigned mode only).
  const [returnNote, setReturnNote] = useState('')

  // Allocate-with-remainder → absorption: when the operator applies without
  // placing the full source amount, we prompt to absorb the remainder (always a
  // Partial write-off). The pending batch is stashed here while they confirm,
  // alongside the note they type in the prompt (textarea B) — keeping it on the
  // prompt object means cancelling clears it for free.
  const [absorptionPrompt, setAbsorptionPrompt] = useState<{
    transactionId: string
    items: AllocateBatchItem[]
    remainder: number
    note: string
  } | null>(null)

  // Preselect the first transaction whenever the detail (re)loads for a new
  // cell/open. Keyed on cell identity + first txId so a same-data refetch
  // doesn't clobber an operator's row selection.
  // Preselect a transaction whenever the detail (re)loads for a new cell/open.
  // Prefer the deep link's target (the row the operator jumped from on the
  // Transaction Details ledger) when it's present in the loaded cell; otherwise
  // fall back to the first transaction. `initialTxId` is value-stable (a memo
  // returning a primitive), so the keyed effect still doesn't clobber a manual
  // row selection on a same-data refetch.
  const firstTxId = transactions[0]?.transactionId ?? null
  const preselectTxId = cellTarget?.preselectTransactionId ?? null
  const initialTxId = useMemo(
    () =>
      preselectTxId &&
      transactions.some((t) => t.transactionId === preselectTxId)
        ? preselectTxId
        : firstTxId,
    [preselectTxId, firstTxId, transactions]
  )
  const cellBenefitId = cellTarget?.benefitId
  const cellPeriod = cellTarget?.periodStartDate
  const cellBenefitLabel = cellTarget?.benefitLabel
  // The benefit behind this allocation is outside recon-v2's benefits
  // projection, so `benefitLabel` is the bare id. Only an explicit `false`
  // counts — an older server omits the flag entirely.
  const benefitUnresolved = cellTarget?.benefitResolved === false
  const cellBenefitDisplayLabel = cellBenefitId
    ? benefitNameLabel({
        benefitId: cellBenefitId,
        benefitLabel: cellBenefitLabel,
        benefitResolved: cellTarget?.benefitResolved,
      })
    : undefined
  useEffect(() => {
    if (!open) return
    setSelectedTxId(initialTxId)
  }, [open, initialTxId, cellBenefitId, cellPeriod])

  // Clear the adjustment note on a fresh open / different cell / different
  // selected transaction. `selectedTxId` is keyed here (not activeTx.id) so:
  // (a) it's declared before this effect; (b) it only changes when the
  // operator explicitly clicks a different TxnRow, not on same-data refetches.
  useEffect(() => {
    if (!open) return
    setAdjustNote('')
    setAbsorbNote('')
    setReturnNote('')
    setAbsorptionPrompt(null)
  }, [open, cellBenefitId, cellPeriod, selectedTxId])

  const activeTx =
    transactions.find((t) => t.transactionId === selectedTxId) ??
    transactions[0] ??
    null

  // A transaction is "split" when its full amount is spread across more than
  // one benefit-month (multiple live slices), i.e. this period holds only part
  // of it. Drives the split glyph on the source card + the detail header.
  const isSplitTx = (tx: (typeof transactions)[number]): boolean =>
    tx.slices.length > 1 ||
    Math.abs(Math.abs(tx.fullAmount) - Math.abs(tx.amountInThisPeriod)) >
      EPSILON

  // Unassigned mode derives its source straight from the target; cell mode
  // reads it from the selected parent transaction (source total = the parent
  // tx's FULL amount, not just this month's slice).
  const sourceAmount = unassigned
    ? Math.abs(unassigned.amount)
    : activeTx
      ? Math.abs(activeTx.fullAmount)
      : 0
  const sourceSign = unassigned
    ? unassigned.amount < 0
      ? -1
      : 1
    : activeTx && activeTx.fullAmount < 0
      ? -1
      : 1
  const sourceClassification: AllocationClassification | null = unassigned
    ? null
    : (activeTx?.slices[0]?.classification ?? null)
  const sourceConf = unassigned
    ? unassigned.conf
    : (detail?.confirmationCode ?? null)

  // Processor status/type of the source transaction. Only unassigned targets
  // carry these, so this is null in cell and manual mode and the rail renders
  // nothing rather than two empty fields.
  const txLabels = sourceTxLabels(unassigned)

  // In unassigned mode there is no allocation to fetch — the source is ready
  // as soon as we have a target; cell mode waits for the transactions list.
  const sourceReady = unassigned ? true : !!activeTx

  // The cell's month total: sum of every transaction's portion allocated to
  // THIS benefit-month. The header shows this, not the selected tx's total.
  const monthTotal = useMemo(
    () => transactions.reduce((s, t) => s + t.amountInThisPeriod, 0),
    [transactions]
  )

  // Overpaid cell: a single, whole (non-split) transaction allocated above the
  // cell's expected. The operator can absorb the excess (paid − expected) —
  // reduce the allocation to expected and write off the difference as a Partial
  // absorption, atomically via allocateBatch. Deliberately NOT offered for
  // split / multi-tx / expected-unknown cells: there the operator reduces the
  // amount in Allocate and the remainder prompt absorbs the leftover.
  // Left summary panel: Total / Expected / Explicit credit / Net due (+ shortfall).
  // The credited term is derived as `expected − netDue`, both server-provided,
  // so the printed lines are arithmetically consistent with the number the
  // server derived this cell's issue from. This is also the single place the
  // net-due-or-expected fallback is resolved — every consumer below reads it
  // off `moneySummary` rather than re-deriving it.
  const moneySummary = computePeriodMoneySummary({
    totalPaid: monthTotal,
    expected: detail?.expectedAmount ?? null,
    creditAmounts: (detail?.credits ?? []).map((c) => c.amount),
    netDue: detail?.netDue,
  })

  // Absorb measures the excess against the raw EXPECTED price, never against net
  // due. Trimming an allocation to net due would subtract the credit from money
  // we received rather than from what we expected — on expected $1,000 /
  // credit $100 / paid $1,000 it would write off the $100 the member overpaid
  // while the $100 credit we owe them still stands, counting it twice. A period
  // whose only excess IS an unconsumed credit is settled by invoicing claiming
  // that credit, which is out of scope here, not by a company write-off.
  const cellExpected = cellTarget ? (detail?.expectedAmount ?? null) : null
  const isOverpaidCell =
    !!cellTarget &&
    !!activeTx &&
    transactions.length === 1 &&
    !isSplitTx(activeTx) &&
    cellExpected != null &&
    cellExpected > 0 &&
    monthTotal > cellExpected + EPSILON
  const overpayExcess =
    isOverpaidCell && cellExpected != null
      ? +(monthTotal - cellExpected).toFixed(2)
      : 0

  // Pinned so the source benefit renders in the picker before any search.
  // Nothing to pin when the benefit is unresolved: the picker searches the same
  // projection the benefit fell out of, so pinning it would show a benefit that
  // cannot be found, re-picked, or offered a month. The cell then allocates like
  // an unassigned transaction — pick a current benefit and Apply, which carries
  // replaceEntireTransaction and so clears the dead slice in the same call.
  const sourceBenefitOption: BenefitOption | null =
    cellTarget && !benefitUnresolved
      ? { benefitId: cellTarget.benefitId, label: cellTarget.benefitLabel }
      : cellTarget
        ? null
        : (unassigned?.prefillBenefit ?? null)

  // Year the allocation belongs to — scopes the benefit picker to that year, so
  // it only offers benefits the Benefits grid would show for the same year. Cell
  // mode uses the period being allocated; unassigned mode uses the tx date.
  const scopeYearSource = cellTarget?.periodStartDate
    ? cellTarget.periodStartDate
    : (activeTx?.transactionDate ?? unassigned?.date)
  const parsedScopeYear = scopeYearSource ? dayjs(scopeYearSource) : null
  const scopeYear =
    parsedScopeYear && parsedScopeYear.isValid()
      ? parsedScopeYear.year()
      : undefined

  // Source type badge (design `srcTypeStyle`): refunds are negative; EE Paid
  // comes off the selected transaction's rail. Unassigned rows carry no source
  // rail, so only refunds badge there.
  const sourceType: 'ee_paid' | 'refund' | null =
    sourceSign < 0
      ? 'refund'
      : activeTx?.source === 'ee_paid'
        ? 'ee_paid'
        : null

  const [groups, setGroups] = useState<GroupState[]>([])

  // Real issue state for the cell (unassigned/manual targets carry none).
  // `hasRealIssue` means the backend auto-detected an over-/under-payment
  // discrepancy for this benefit-month AND it's still unresolved. Once resolved,
  // the cell behaves like a clean cell for flagging purposes so operators can
  // add a persisted manual-review flag on top.
  const issueType = cellTarget ? (detail?.issue?.type ?? null) : null
  const issueResolved = detail?.issue?.resolved ?? false
  const hasRealIssue = issueType !== null && !issueResolved

  // Persisted manual-review flag, read from the SAME materialized source the
  // grid cell reads (period_allocation_issue via period-detail's manualFlagged),
  // so the drawer's flag button and the grid cell can't disagree. Only meaningful
  // when the cell has no unresolved auto-detected issue (auto issues own the flag then).
  const isManuallyFlagged = !hasRealIssue && !!detail?.manualFlagged

  // Flag state drives the red amount/icon styling. For cells with a real
  // discrepancy it mirrors the backend issue state (unresolved → flagged);
  // for clean cells it reflects the persisted manual-review flag.
  const [flagged, setFlagged] = useState(false)

  // Re-derive the initial flag whenever the modal opens on a (new) cell, the
  // period detail arrives, or the manual-flag data loads:
  // - Unresolved auto discrepancy → flagged
  // - Persisted manual flag → flagged
  // - Otherwise → unflagged
  useEffect(() => {
    if (!open) return
    setFlagged(hasRealIssue || isManuallyFlagged)
  }, [open, cellBenefitId, cellPeriod, hasRealIssue, isManuallyFlagged])

  // Refund reason picker — only shown/used when the source is a refund
  // (sourceSign < 0). Seeded from the existing classification when it is
  // already a REFUND_* value (cell-mode re-adjust), otherwise REFUND_OTHER.
  const [refundReason, setRefundReason] = useState<RefundReason>(
    AllocationClassification.REFUND_OTHER
  )

  // Reset the picker whenever the modal opens on a new cell or transaction so
  // stale picks from a prior session don't carry over.
  useEffect(() => {
    if (!open) return
    setRefundReason(
      isRefundReason(sourceClassification)
        ? sourceClassification
        : AllocationClassification.REFUND_OTHER
    )
  }, [open, cellBenefitId, cellPeriod, selectedTxId, sourceClassification])

  // Manual "Add Payment" form state.
  const [manualForm, setManualForm] = useState<ManualFormState>(emptyManualForm)
  // Single-open accordion for the adjust view's sections. `null` = everything
  // collapsed. The stored key may not exist for the current mode (e.g.
  // 'allocate' on an empty cell) — `effectiveSection` below falls back to the
  // first available section, so the default open resolves to Allocate when
  // present, else Add payment.
  // Sections start collapsed — the operator opens the one they want.
  const [activeSection, setActiveSection] = useState<AdjustSectionKey | null>(
    null
  )
  const initialSection = unassigned?.initialSection

  useEffect(() => {
    if (!open) return
    // Prefill "Add payment" from the benefit-month cell the operator opened, so
    // an EE Paid / refund starts on that benefit + month instead of an empty
    // picker. BenefitSelect pins its `value`, so the label renders even before
    // any search. Manual mode (no cell) still opens blank.
    setManualForm({
      ...emptyManualForm(),
      // Same rule as the allocate picker: an unresolved benefit is not
      // selectable, so prefilling it would only have to be cleared.
      benefit:
        cellBenefitId && !benefitUnresolved
          ? {
              benefitId: cellBenefitId,
              label: cellBenefitDisplayLabel ?? cellBenefitId,
            }
          : null,
      month: cellPeriod ?? null,
    })
    // Sections start collapsed — the operator opens the one they want — unless
    // the caller asked for one (the Return row button lands on Return).
    setActiveSection(initialSection ?? null)
  }, [
    open,
    initialSection,
    cellBenefitId,
    cellPeriod,
    cellBenefitDisplayLabel,
    benefitUnresolved,
  ])

  // Sections available in the current mode: Allocate needs a loaded source
  // transaction; Set expected needs a cell. Add payment is offered for a cell
  // (record an out-of-band payment onto that benefit-month) but NOT for an
  // unassigned/floating transaction — there the only action is to allocate the
  // existing amount to a benefit, not to invent a new payment.
  // Absorb section appears in two situations: a FULL write-off of a tx with no
  // live allocations (unassigned/unallocated), or a PARTIAL write-off of an
  // overpaid cell's excess (see isOverpaidCell). A partially-allocated tx that
  // isn't overpaid has no absorb affordance — its leftover is absorbed through
  // the allocate-with-remainder prompt.
  // Explicit credit is cell-mode only. RECORDING one needs at least one live
  // allocation on the period — the server rejects a credit without one — but an already-credited period keeps its credits when its last
  // allocation is deallocated ("the period goes quiet, but the debt to the
  // member survives"). So the section is still offered there, read-only bar the
  // remove control, rather than stranding credits with no way to see or delete
  // them. The entry row itself is suppressed below on the same condition.
  const hasCreditsOnPeriod = (detail?.credits?.length ?? 0) > 0
  const hasLiveAllocations = !unassigned && (activeTx?.slices?.length ?? 0) > 0
  const availableSections: AdjustSectionKey[] = [
    ...(sourceReady ? (['allocate'] as const) : []),
    ...(unassigned ? [] : (['addPayment'] as const)),
    ...(cellTarget ? (['setExpected'] as const) : []),
    ...(sourceReady && (!hasLiveAllocations || isOverpaidCell)
      ? (['absorb'] as const)
      : []),
    ...(cellTarget && (hasLiveAllocations || hasCreditsOnPeriod)
      ? (['explicitCredit'] as const)
      : []),
    // Return: an unassigned iStream payment only. EPS also rejects an absorbed
    // tx, so a partial absorb surfaces as an error toast on submit.
    ...(unassigned && isReturnableSource(unassigned.source)
      ? (['return'] as const)
      : []),
  ]
  const effectiveSection: AdjustSectionKey | null =
    activeSection === null
      ? null
      : availableSections.includes(activeSection)
        ? activeSection
        : (availableSections[0] ?? null)

  const allocateBatch = useAllocateBatch({
    onError: (err: unknown) => {
      message.error(`Failed to adjust payment: ${extractApiErrorMessage(err)}`)
    },
  })

  const recordAbsorption = useRecordAbsorption({
    onError: (err: unknown) => {
      message.error(
        `Failed to record absorption: ${extractApiErrorMessage(err)}`
      )
    },
  })

  const eePaid = useEePaid({
    onError: (err: unknown) => {
      message.error(
        `Failed to add EE Paid transaction: ${extractApiErrorMessage(err)}`
      )
    },
  })

  const manualRefund = useRecordManualRefund({
    onError: (err: unknown) => {
      message.error(`Failed to add refund: ${extractApiErrorMessage(err)}`)
    },
  })

  const resolveIssue = useResolveIssue({
    onError: () => message.error('Failed to resolve issue'),
  })

  const flagIssue = useFlagIssue()
  const unflagIssue = useUnflagIssue()

  const deallocate = useDeallocate({
    onError: (err: unknown) => {
      message.error(
        `Failed to update allocation: ${extractApiErrorMessage(err)}`
      )
    },
  })

  const returnTransaction = useReturnTransaction({
    onError: (err: unknown) => {
      message.error(`Failed to return payment: ${extractApiErrorMessage(err)}`)
    },
  })

  const deleteManualPayment = useDeleteManualPayment({
    onError: (err: unknown) => {
      message.error(`Failed to delete payment: ${extractApiErrorMessage(err)}`)
    },
  })

  const setExpectedPrice = useSetExpectedPrice()

  // Set Expected Price form state (cell mode only).
  const [setExpectedAmount, setSetExpectedAmount] = useState('')
  const [setExpectedNote, setSetExpectedNote] = useState('')

  // ----- Explicit credit (cell mode only) -----

  // The markup lives in ExplicitCreditSection; only the state does not. The
  // accordion unmounts a collapsed body, so a half-typed credit and a server
  // rejection survive a mis-clicked header only by being held here, alongside
  // the sibling write forms above.
  const explicitCreditForm = useExplicitCreditForm({
    benefitId: cellBenefitId,
    periodStartDate: cellPeriod,
  })
  const credits = cellTarget ? (detail?.credits ?? []) : []

  // Reset on each new open/cell so stale values don't carry over.
  useEffect(() => {
    if (!open) return
    setSetExpectedAmount('')
    setSetExpectedNote('')
    setExpectedPrice.reset()
    explicitCreditForm.reset()
  }, [open, cellBenefitId, cellPeriod])

  const setExpectedAmountNormalized = normalizeExpectedAmount(setExpectedAmount)
  const setExpectedValid = !!cellTarget && setExpectedAmountNormalized !== null

  const handleSetExpected = async () => {
    if (!cellBenefitId || !cellPeriod || !setExpectedAmountNormalized) return
    const req = buildSetExpectedRequest({
      benefitId: cellBenefitId,
      periodStartDate: cellPeriod,
      rawAmount: setExpectedAmount,
      note: setExpectedNote.trim() || undefined,
    })
    if (!req) return
    try {
      await setExpectedPrice.mutateAsync(req)
      message.success('Expected price updated')
      setSetExpectedAmount('')
      setSetExpectedNote('')
    } catch {
      /* error shown via Alert below */
    }
  }

  // Seed the group(s) whenever the drawer (re)opens for a target OR the
  // operator selects a different parent transaction. Cell mode: reconstruct
  // the selected parent — one group per distinct benefit the tx is split
  // across, each pre-filled with its month-rows at the current slice amounts.
  // The clicked benefit is ordered first. Re-splitting from here wipes the
  // whole selected tx (replaceEntireTransaction), so no split-after-split.
  const activeTxId = activeTx?.transactionId
  const activeSlicesKey = useMemo(
    () =>
      activeTx
        ? activeTx.slices
            .map((s) => `${s.benefitId}|${s.periodStartDate}|${s.amount}`)
            .join(',')
        : '',
    [activeTx]
  )
  useEffect(() => {
    if (!open || !activeTx || sourceAmount <= 0) return
    const byBenefit = new Map<string, GroupState>()
    activeTx.slices.forEach((s) => {
      // An unresolved benefit cannot be re-picked or offered a month, so seeding
      // its slice would hand the operator a group they can only delete. Leave
      // its money unplaced instead: it shows up in "Remaining to allocate" and
      // the empty group below takes it to a benefit that still exists.
      if (s.benefitResolved === false) return
      let group = byBenefit.get(s.benefitId)
      if (!group) {
        group = {
          id: s.benefitId,
          benefit: { benefitId: s.benefitId, label: s.benefitLabel },
          rows: {},
        }
        byBenefit.set(s.benefitId, group)
      }
      group.rows[s.periodStartDate] = Math.abs(Number(s.amount)).toFixed(2)
    })
    // Map preserves first-seen order; move the clicked benefit to the front.
    const seeded = Array.from(byBenefit.values()).sort((a, b) =>
      a.benefit?.benefitId === cellBenefitId
        ? -1
        : b.benefit?.benefitId === cellBenefitId
          ? 1
          : 0
    )
    // Every slice was unresolved — fall back to one empty group so the section
    // opens on a searchable picker rather than on nothing at all.
    setGroups(
      seeded.length > 0
        ? seeded
        : [{ id: 'unresolved-source', benefit: null, rows: {} }]
    )
    // Deps key on activeTxId + activeSlicesKey (a slice-content signature)
    // rather than the `activeTx` object, so operator edits aren't clobbered
    // unless the selected transaction (or its slices) actually changes.
  }, [open, activeTxId, activeSlicesKey, cellBenefitId, sourceAmount])

  // Unassigned mode: one group pre-selecting `prefillBenefit` (refunds with a
  // known benefit) or an empty group otherwise. No source month/amount is
  // pre-filled — there's no source period to reallocate from.
  const unassignedTxId = unassigned?.transactionId
  // The review list's prefillBenefit is a card-latest hint and can point at the
  // wrong plan year for a card reused across years. Resolve the transaction-date-
  // aware benefit on open (matching what auto-allocate would write) and swap it in
  // once it arrives — but never over operator edits (see the second effect). Runs
  // for every unassigned tx with a benefit to resolve (refunds, auto-allocate-
  // failed, pending-allocation), not just refunds.
  const hintBenefitId = unassigned?.prefillBenefit?.benefitId
  const hintLabel = unassigned?.prefillBenefit?.label
  const { data: resolvedTxBenefit } = useResolveTransactionBenefit(
    unassignedTxId,
    { enabled: open && !!unassignedTxId }
  )

  // The benefit the read-only year strip is about. Cell mode always knows it;
  // unassigned mode prefers the DATE-AWARE resolution over the list's card-latest
  // hint, matching what auto-allocate would have written — and is simply null
  // for a transaction that resolved to nothing, which is when the strip hides.
  const stripBenefitId =
    cellBenefitId ??
    resolvedTxBenefit?.benefitId ??
    unassigned?.prefillBenefit?.benefitId ??
    null

  // Amount for the strip's `useSuggestPeriods`, which refuses to fire without
  // one — the ranking it buys (`score`, `reasons`) is discarded by the strip.
  //
  // Prefer the source amount, because the Allocate section passes exactly that
  // and the two then share a single request. But an underpaid month with
  // NOTHING allocated yet has no transaction and so no source amount — and that
  // is a Flagged-items row operators open constantly, the primary case for the
  // strip existing. Falling back costs no sharing there: `sourceReady` is false
  // without an `activeTx`, so the Allocate section is not even offered and
  // there is no second caller to hash alongside.
  const stripAmount = sourceAmount > 0 ? sourceAmount : (cellExpected ?? 0)

  // Initialize the source group ONCE per open, from the fast list hint. Depends
  // only on open/txId/hint — not on the async resolve — so it never re-runs when
  // the resolved benefit lands and can't wipe in-progress edits.
  useEffect(() => {
    if (!open || !unassignedTxId) return
    setGroups([
      {
        id: 'source',
        benefit: hintBenefitId
          ? { benefitId: hintBenefitId, label: hintLabel ?? '' }
          : null,
        rows: {},
      },
    ])
  }, [open, unassignedTxId, hintBenefitId, hintLabel])
  // When the date-aware benefit resolves, swap it into the source group — but
  // ONLY if the operator hasn't touched it (still the hint, no rows selected).
  // This is the whole point of the feature (cross-year card → correct benefit)
  // while guaranteeing it never clobbers a manual pick or entered amounts.
  useEffect(() => {
    if (!resolvedTxBenefit?.benefitId) return
    setGroups((prev) => {
      if (prev.length !== 1 || prev[0].id !== 'source') return prev
      const g = prev[0]
      const untouched =
        Object.keys(g.rows).length === 0 &&
        (g.benefit == null || g.benefit.benefitId === hintBenefitId)
      if (!untouched || g.benefit?.benefitId === resolvedTxBenefit.benefitId) {
        return prev
      }
      const p = resolvedTxBenefit.insuredPersons[0]
      const label = p
        ? `${p.firstName} ${p.lastName}`.trim()
        : resolvedTxBenefit.employeeName || resolvedTxBenefit.benefitId
      return [
        { ...g, benefit: { benefitId: resolvedTxBenefit.benefitId, label } },
      ]
    })
  }, [resolvedTxBenefit, hintBenefitId])

  const updateGroup = useCallback(
    (id: string, fn: (g: GroupState) => GroupState) =>
      setGroups((gs) => gs.map((g) => (g.id === id ? fn(g) : g))),
    []
  )

  const handleBenefitChange = useCallback(
    (id: string, benefit: BenefitOption) =>
      // Switching the benefit invalidates any month picked for the previous
      // one — clear the group's rows.
      updateGroup(id, (g) => ({ ...g, benefit, rows: {} })),
    [updateGroup]
  )

  const handleToggleMonth = useCallback(
    (id: string, period: string) =>
      updateGroup(id, (g) => {
        if (period in g.rows) {
          const rows = { ...g.rows }
          delete rows[period]
          return { ...g, rows }
        }
        return { ...g, rows: { ...g.rows, [period]: '' } }
      }),
    [updateGroup]
  )

  const handleRowAmount = useCallback(
    (id: string, period: string, amount: string) =>
      updateGroup(id, (g) => ({
        ...g,
        rows: { ...g.rows, [period]: amount },
      })),
    [updateGroup]
  )

  const handleRemoveRow = useCallback(
    (id: string, period: string) =>
      updateGroup(id, (g) => {
        const rows = { ...g.rows }
        delete rows[period]
        return { ...g, rows }
      }),
    [updateGroup]
  )

  // Split evenly divides the budget still AVAILABLE to this group (the source
  // amount minus what every other group already holds) across this group's rows
  // (to the cent; rounding drift lands on the first row). Splitting the full
  // source would over-allocate a payment already partly placed in other groups.
  const handleSplitEvenly = useCallback(
    (id: string) =>
      setGroups((gs) => {
        const otherGroupsTotal = gs
          .filter((gr) => gr.id !== id)
          .reduce((s, gr) => s + sumRows(gr.rows), 0)
        const available = +(sourceAmount - otherGroupsTotal).toFixed(2)
        return gs.map((g) => {
          if (g.id !== id) return g
          // splitEvenlyRows returns null for a non-positive amount, so a group
          // whose budget is already consumed by others is left unchanged.
          const rows = splitEvenlyRows(Object.keys(g.rows), available)
          return rows ? { ...g, rows } : g
        })
      }),
    [sourceAmount]
  )

  const handleRemoveGroup = useCallback(
    (id: string) => setGroups((gs) => gs.filter((g) => g.id !== id)),
    []
  )

  const handleAddGroup = useCallback(
    () =>
      setGroups((gs) => [
        ...gs,
        { id: `g-${Date.now()}-${gs.length}`, benefit: null, rows: {} },
      ]),
    []
  )

  const insuredLabel = useMemo(() => {
    const p = cellTarget?.insuredPersons?.[0]
    // No insured person to name. An unresolved benefit has no name to fall back
    // to either — printing its id here would pass a UUID off as a member.
    if (!p) return cellBenefitDisplayLabel ?? cellTarget?.benefitLabel ?? ''
    const rel =
      p.relationship && p.relationship !== 'employee'
        ? ` (${p.relationship})`
        : ''
    return `${p.firstName} ${p.lastName}${rel}`.trim()
  }, [cellTarget, cellBenefitDisplayLabel])

  // Per-group subtraction terms for the equation (only groups that carry a
  // benefit + at least one amount).
  const equationTerms = useMemo(
    () =>
      groups
        .map((g) => ({
          id: g.id,
          label: g.benefit?.label ?? 'Benefit',
          total: sumRows(g.rows),
        }))
        .filter((t) => t.total > 0),
    [groups]
  )

  // Full "(Source) − (Benefit)… = Remain" equation only once the payment is
  // split across ≥2 funded groups; a single group gets the simple remaining
  // line — per the design's `showFullEquation`.
  const showFullEquation = equationTerms.length >= 2

  const allocated = useMemo(
    () => groups.reduce((s, g) => s + sumRows(g.rows), 0),
    [groups]
  )
  const remainder = +(sourceAmount - allocated).toFixed(2)
  const overAllocated = remainder < -EPSILON
  const hasRemainder = remainder > EPSILON
  // Remainder color state drives the shared .over/.exact/.remaining modifier
  // used by both the equation amount and its hint.
  const remainderState: 'over' | 'remaining' | 'exact' = overAllocated
    ? 'over'
    : hasRemainder
      ? 'remaining'
      : 'exact'

  // The whole source payment must be placed — no leftover and no over-shoot.
  // (This is total-vs-source; it does not restrict per-period paid-vs-expected,
  // where over-allocation remains allowed by design.)
  const isFullyAllocated = Math.abs(remainder) <= EPSILON
  const hasBenefitedItem = groups.some(
    (g) => !!g.benefit && Object.values(g.rows).some((v) => Number(v) > 0)
  )
  // Baseline = the allocation as it was loaded (cell mode: the selected
  // transaction's existing slices; unassigned mode: nothing). Apply stays
  // disabled until the operator actually changes the allocation, so an
  // untouched cell can't be re-applied as a no-op.
  const currentAllocationSig = useMemo(
    () => allocationSignature(groups),
    [groups]
  )
  const baselineAllocationSig = useMemo(() => {
    if (unassigned || !activeTx) return ''
    return activeTx.slices
      .filter((s) => Math.abs(Number(s.amount)) > 0)
      .map(
        (s) =>
          `${s.benefitId}|${s.periodStartDate}|${Math.abs(Number(s.amount)).toFixed(2)}`
      )
      .sort()
      .join(',')
  }, [unassigned, activeTx])
  const isAllocationDirty = currentAllocationSig !== baselineAllocationSig
  // Note and refund-reason edits are also changes worth applying. Note baseline
  // is always empty (reset on open); refund-reason baseline is the seeded value
  // (existing REFUND_* classification, else REFUND_OTHER) and only applies to
  // refund sources, which is the only case the picker is shown.
  const isNoteDirty = adjustNote.trim().length > 0
  const baselineRefundReason: RefundReason = isRefundReason(
    sourceClassification
  )
    ? sourceClassification
    : AllocationClassification.REFUND_OTHER
  const isRefundDirty = sourceSign < 0 && refundReason !== baselineRefundReason
  const isDirty = isAllocationDirty || isNoteDirty || isRefundDirty
  // Apply is allowed when the placed total is exact OR under the source (the
  // under case absorbs the remainder). Over-allocation stays blocked — you can
  // never place more than the source amount.
  const canApply = sourceReady && !overAllocated && hasBenefitedItem && isDirty

  const copyTransactionNumber = useCallback(
    (transactionNumber: string | null | undefined) => {
      if (!transactionNumber) return
      void navigator.clipboard.writeText(transactionNumber)
      message.success('Copied transaction number')
    },
    []
  )

  const handleCopyConf = useCallback(() => {
    if (!sourceConf) return
    void navigator.clipboard.writeText(sourceConf)
    message.success('Copied confirmation code')
  }, [sourceConf])

  // Manually-added transactions (EE Paid, and Check / Group ACH refunds) are
  // deletable from the modal. Cell mode only — unassigned rows and rail-sourced
  // (iStream / Marqeta / WEX) payments never show the trash. Delete acts on the
  // SELECTED transaction.
  const canDeleteTxn =
    !!cellTarget && SOURCE_ABLE_TO_DELETE.includes(activeTx?.source ?? '')

  const handleDeleteTxn = useCallback(() => {
    const transactionId = activeTxId
    if (!transactionId) return
    Modal.confirm({
      title: 'Delete transaction?',
      content:
        'This removes the manually added transaction and all of its allocations across every month and benefit it was split into.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          // Manual sources only (canDeleteTxn gate). Clear EVERY live slice first
          // (backend logs a DEALLOCATED event per slice), THEN delete the payment
          // itself — deleteManualPayment rejects a still-allocated payment, so the
          // two steps must run in order. Without the second call the manual
          // payment row survives and reappears in the review queue.
          await deallocate.mutateAsync({ transactionId })
          await deleteManualPayment.mutateAsync({ transactionId })
          message.success('Transaction deleted')
          onClose()
        } catch {
          /* onError handler shows the toast */
        }
      },
    })
  }, [activeTxId, deallocate, deleteManualPayment, onClose])

  // Unassigned manual payment (EE Paid / Check / Group ACH from the review
  // queue): can be permanently deleted. deallocate would be a no-op here (no
  // allocation), so this hits the dedicated delete-payment endpoint that removes
  // the source + parent rows. Rail-sourced unassigned txns are ingested and
  // cannot be deleted.
  const canDeleteUnassigned =
    !!unassigned && SOURCE_ABLE_TO_DELETE.includes(unassigned.source ?? '')

  const handleDeleteUnassigned = useCallback(() => {
    const transactionId = unassigned?.transactionId
    if (!transactionId) return
    Modal.confirm({
      title: 'Delete payment?',
      content:
        'This permanently deletes the manually added payment. This cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteManualPayment.mutateAsync({ transactionId })
          message.success('Payment deleted')
          onClose()
        } catch {
          /* onError handler shows the toast */
        }
      },
    })
  }, [unassigned, deleteManualPayment, onClose])

  // Every allocated transaction can have its allocation removed — un-assigning
  // it back to the review queue. `hold: true` keeps the auto-allocate sweep from
  // reclaiming it until re-assigned. Cell mode only (unassigned rows have no
  // allocation to remove). Delete (below) is the harder action, manual txns only.
  const canRemoveAllocation = !!cellTarget && !!activeTx

  const handleRemoveAllocation = useCallback(() => {
    const transactionId = activeTxId
    if (!transactionId) return
    Modal.confirm({
      title: 'Remove allocation?',
      content:
        'This un-assigns the transaction from every month and benefit it was allocated to and moves it back to the review queue. It is held from auto-allocation until you re-assign it.',
      okText: 'Remove allocation',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deallocate.mutateAsync({ transactionId, hold: true })
          message.success('Allocation removed')
          onClose()
        } catch {
          /* onError handler shows the toast */
        }
      },
    })
  }, [activeTxId, deallocate, onClose])

  // Flag button.
  // - Unresolved auto discrepancy: un-flagging calls resolveIssue (persisted).
  //   Once resolved, `hasRealIssue` flips to false and re-flagging falls through
  //   to the manual-flag path below (persisted via flag-issue).
  // - Clean / resolved cell: flagging calls flag-issue (persisted); un-flagging
  //   calls unflag-issue (persisted). Both invalidate all recon-v2 queries.
  // - Unassigned/manual mode: no cell → button is not shown.
  const handleToggleFlag = useCallback(() => {
    if (hasRealIssue && cellBenefitId && cellPeriod && flagged) {
      resolveIssue.mutate(
        { benefitId: cellBenefitId, periodStartDate: cellPeriod },
        { onSuccess: () => setFlagged(false) }
      )
      return
    }
    if (!cellBenefitId || !cellPeriod) {
      setFlagged((f) => !f)
      return
    }
    if (isManuallyFlagged) {
      unflagIssue.mutate(
        { benefitId: cellBenefitId, periodStartDate: cellPeriod },
        { onSuccess: () => setFlagged(false) }
      )
    } else {
      flagIssue.mutate(
        { benefitId: cellBenefitId, periodStartDate: cellPeriod },
        { onSuccess: () => setFlagged(true) }
      )
    }
  }, [
    hasRealIssue,
    cellBenefitId,
    cellPeriod,
    flagged,
    isManuallyFlagged,
    resolveIssue,
    flagIssue,
    unflagIssue,
  ])

  // Any in-flight flag mutation (resolve / flag / unflag) — drives both the
  // flag button's disabled state and its inline spinner.
  const isFlagMutating =
    resolveIssue.isPending || flagIssue.isPending || unflagIssue.isPending

  // Which verb the success toasts use, decided once. Both Required-action arms
  // reach here — an unposted transaction as an `unassigned` target, a
  // discrepancy as a CELL target — and neither may come back saying
  // "Reallocated" under a button that said Allocate. Only the transaction arm
  // promises the verb outright; see allocationVerb for why the cell cannot.
  const allocationOutcome = {
    promisedAction: unassigned?.promisedAction,
    isQueueCell: !!cellTarget?.fromRequiredActionQueue,
    isRefund: sourceSign < 0,
  }

  const handleApply = async () => {
    // Cell mode applies to the SELECTED parent transaction — the whole tx
    // re-splits (replaceEntireTransaction), other cell txns are untouched.
    const transactionId = unassigned ? unassigned.transactionId : activeTxId
    if (!transactionId) {
      message.error('Source payment not loaded yet')
      return
    }

    // One slice per (group benefit, month-row) with a positive amount. Refund
    // sources stay negative via sourceSign; source classification is preserved.
    const items: AllocateBatchItem[] = []
    groups.forEach((g) => {
      const benefit = g.benefit
      if (!benefit) return
      Object.entries(g.rows).forEach(([period, raw]) => {
        const n = Number(raw)
        if (!Number.isFinite(n) || n <= 0) return
        items.push({
          benefitId: benefit.benefitId,
          periodStartDate: period,
          amount: (n * sourceSign).toFixed(2),
          // Resolve the classification via the shared helper. Refund slices
          // carry the operator's picked reason (or REFUND_OTHER as default);
          // charge slices preserve the source classification or omit it.
          classification: resolveSliceClassification(
            sourceSign,
            sourceClassification,
            refundReason
          ),
        })
      })
    })

    if (items.length === 0) {
      message.error('Add at least one allocation before applying')
      return
    }

    // Attach the optional note to the first slice only — the backend appends
    // one NOTE_ADDED event per item that carries a note, so spreading the same
    // note across every slice would duplicate it on the timeline.
    const trimmedNote = adjustNote.trim()
    if (trimmedNote.length > 0 && items[0]) {
      items[0] = { ...items[0], note: trimmedNote }
    }

    // Over-allocation is never valid — you cannot place more than the source.
    if (overAllocated) {
      message.error(
        `Over-allocated by ${fmtMoney(Math.abs(remainder))} — the most you can place is ${fmtMoney(sourceAmount)}.`
      )
      return
    }

    // Under-allocation is the absorption case: the operator placed the correct
    // amount and the leftover is what we overpaid. Prompt to write it off before
    // committing — the absorption is applied atomically with the allocation.
    if (hasRemainder) {
      setAbsorptionPrompt({ transactionId, items, remainder, note: '' })
      return
    }

    try {
      await allocateBatch.mutateAsync({
        transactionId,
        items,
        // Cell mode reconstructs the whole SELECTED parent tx, so wipe ALL its
        // live slices before re-writing — re-adjusting always starts from the
        // original transaction, never a prior split. Unassigned mode is a fresh
        // allocation with nothing to remove.
        ...(cellTarget ? { replaceEntireTransaction: true } : {}),
      })
      message.success(
        allocationSuccessMessage({
          ...allocationOutcome,
          sliceCount: items.length,
        })
      )
      onClose()
    } catch {
      /* onError handler shows the toast */
    }
  }

  // Confirm the allocate-with-remainder write-off: submit the stashed batch plus
  // the absorption sibling in one atomic call.
  const handleConfirmAbsorption = async () => {
    if (!absorptionPrompt) return
    // Defence in depth behind the disabled OK button: an absorption is
    // re-recorded from this body on every submit, so an empty note here stores
    // NULL with no error to notice (ADR 0003).
    if (!isAbsorptionNoteValid(absorptionPrompt.note)) return
    try {
      await allocateBatch.mutateAsync({
        transactionId: absorptionPrompt.transactionId,
        items: absorptionPrompt.items,
        ...(cellTarget ? { replaceEntireTransaction: true } : {}),
        // The allocate-leftover write-off is always a partial absorption.
        absorption: {
          amount: absorptionPrompt.remainder.toFixed(2),
          absorptionType: AbsorptionType.Partial,
          note: absorptionPrompt.note.trim(),
        },
      })
      message.success(
        absorptionSuccessMessage({
          ...allocationOutcome,
          remainder: absorptionPrompt.remainder,
        })
      )
      setAbsorptionPrompt(null)
      onClose()
    } catch {
      /* onError handler shows the toast */
    }
  }

  // The transaction's unallocated remainder (dollars): its full amount minus the
  // sum of its live allocation slices. Absorption can no longer trim an
  // allocation, so the standalone action can only write off what is unallocated.
  // Unassigned mode has no slices, so the whole source amount is available.
  const absorbRemainder = +(
    sourceAmount -
    (activeTx?.slices ?? []).reduce(
      (s, sl) => s + Math.abs(Number(sl.amount)),
      0
    )
  ).toFixed(2)

  // "Absorb" accordion action: a FULL write-off of the whole transaction (only
  // offered when it has no live allocations — a Partial absorb never exists
  // without an allocation; that path is the allocate-with-remainder prompt).
  const handleConfirmAbsorptionAction = async () => {
    const transactionId = unassigned ? unassigned.transactionId : activeTxId
    if (!transactionId) {
      message.error('Source payment not loaded yet')
      return
    }
    if (absorbRemainder <= 0) {
      message.error('Nothing to absorb')
      return
    }
    try {
      await recordAbsorption.mutateAsync({
        transactionId,
        amount: absorbRemainder.toFixed(2),
        absorptionType: AbsorptionType.Full,
        note: absorbNote.trim(),
      })
      message.success(`Absorbed ${fmtMoney(absorbRemainder)}`)
      onClose()
    } catch {
      /* onError handler shows the toast */
    }
  }

  // Absorb an overpaid cell's excess: reduce the allocation to the cell's
  // expected and write off (paid − expected) as a Partial absorption, in one
  // atomic allocateBatch (replaceEntireTransaction re-splits the whole, non-split
  // tx to just this cell at expected; the absorption sibling records the excess).
  // Routed through allocateBatch — NOT recordAbsorption, whose conservation guard
  // rejects on a fully-allocated cell (unallocated remainder is 0).
  const handleAbsorbOverpayment = async () => {
    if (
      !cellTarget ||
      !activeTx ||
      cellExpected == null ||
      overpayExcess <= 0
    ) {
      return
    }
    try {
      await allocateBatch.mutateAsync({
        transactionId: activeTx.transactionId,
        items: [
          {
            benefitId: cellTarget.benefitId,
            periodStartDate: cellTarget.periodStartDate,
            amount: cellExpected.toFixed(2),
            classification: resolveSliceClassification(
              sourceSign,
              sourceClassification,
              refundReason
            ),
          },
        ],
        replaceEntireTransaction: true,
        absorption: {
          amount: overpayExcess.toFixed(2),
          absorptionType: AbsorptionType.Partial,
          // The Absorb section's own note — NOT `adjustNote`, and never also on
          // items[0]: writing it to both prints the same sentence twice in the
          // ledger's Note column (allocation row + absorption sub-row).
          note: absorbNote.trim(),
        },
      })
      message.success(`Absorbed ${fmtMoney(overpayExcess)} overpayment`)
      onClose()
    } catch {
      /* onError handler shows the toast */
    }
  }

  // ----- manual ("Add Payment") derivations -----

  const setManualField = <K extends keyof ManualFormState>(
    key: K,
    value: ManualFormState[K]
  ) => setManualForm((f) => ({ ...f, [key]: value }))

  const manualAmountNum = Number(manualForm.amount)
  const manualAmountValid =
    Number.isFinite(manualAmountNum) && manualAmountNum > EPSILON

  const { data: manualSuggestions, isError: isManualSuggestError } =
    useSuggestPeriods(
      {
        benefitId: manualForm.benefit?.benefitId,
        // Amount only refines the ranking; default to 0 so the month list loads
        // (and can be picked) before an amount is entered.
        amount: manualAmountValid ? manualAmountNum : 0,
        transactionDate: manualForm.date || undefined,
      },
      {
        // Also load when the Add-payment accordion section is open in adjust
        // mode (isManual is false there) — otherwise the month grid stays empty.
        enabled:
          open &&
          (isManual || effectiveSection === 'addPayment') &&
          !!manualForm.benefit,
      }
    )

  const manualValid =
    manualAmountValid &&
    !!manualForm.date &&
    !!manualForm.benefit &&
    !!manualForm.month &&
    // Refunds must carry a rail (and a check number on Check); ee_paid needs none.
    (manualForm.type !== 'refund' || isManualRefundRailValid(manualForm))

  const handleManualSubmit = async (onSuccess: () => void = onClose) => {
    if (!manualValid) return
    const benefit = manualForm.benefit
    const month = manualForm.month
    if (!benefit || !month) return
    const transactionDate = manualForm.date || undefined
    const amount = manualAmountNum.toFixed(2)
    const note = manualForm.note.trim() || undefined
    try {
      if (manualForm.type === 'refund') {
        if (!manualForm.source) return
        await manualRefund.mutateAsync({
          benefitId: benefit.benefitId,
          periodStartDate: month,
          amount,
          source: manualForm.source,
          checkNumber: manualRefundCheckNumber(manualForm),
          reason: manualForm.reason,
          transactionDate,
          note,
        })
        message.success('Refund added')
      } else {
        await eePaid.mutateAsync({
          benefitId: benefit.benefitId,
          periodStartDate: month,
          amount,
          transactionDate,
          note,
        })
        message.success('EE Paid transaction added')
      }
      onSuccess()
    } catch {
      /* onError handler shows the toast */
    }
  }

  const manualBody = (
    <div className={styles.manualForm}>
      <div>
        <div className={styles.fieldLabel}>
          Amount<span className={styles.req}> *</span>
        </div>
        <input
          className={`${styles.formInput} ${styles.moneyInput}`}
          value={manualForm.amount}
          inputMode="decimal"
          placeholder="$0.00"
          onChange={(e) => {
            const raw = e.target.value
            if (isMoneyInput(raw)) setManualField('amount', raw)
          }}
        />
      </div>

      <div>
        <div className={styles.fieldLabel}>
          Type<span className={styles.req}> *</span>
        </div>
        <div className={styles.radioGroupRow}>
          <button
            className={styles.radioBtn}
            type="button"
            onClick={() =>
              // Leaving refund clears the rail so it can't submit stale.
              setManualForm((f) => ({
                ...f,
                type: 'ee_paid',
                source: null,
                checkNumber: '',
              }))
            }
          >
            <span
              className={`${styles.radioDot}${
                manualForm.type === 'ee_paid' ? ` ${styles.active}` : ''
              }`}
            >
              {manualForm.type === 'ee_paid' ? (
                <span className={styles.radioDotInner} />
              ) : null}
            </span>
            EE Paid
          </button>
          <button
            className={styles.radioBtn}
            type="button"
            onClick={() => setManualField('type', 'refund')}
          >
            <span
              className={`${styles.radioDot}${
                manualForm.type === 'refund' ? ` ${styles.active}` : ''
              }`}
            >
              {manualForm.type === 'refund' ? (
                <span className={styles.radioDotInner} />
              ) : null}
            </span>
            Refund
          </button>
        </div>
      </div>

      {manualForm.type === 'refund' ? (
        <div>
          <div className={styles.fieldLabel}>
            Source<span className={styles.req}> *</span>
          </div>
          <div className={styles.radioGroupRow}>
            <button
              className={styles.radioBtn}
              type="button"
              onClick={() =>
                // Group ACH carries no check number; clear it so it can't submit stale.
                setManualForm((f) => ({
                  ...f,
                  source: ManualRefundSource.GroupAch,
                  checkNumber: '',
                }))
              }
            >
              <span
                className={`${styles.radioDot}${
                  manualForm.source === ManualRefundSource.GroupAch
                    ? ` ${styles.active}`
                    : ''
                }`}
              >
                {manualForm.source === ManualRefundSource.GroupAch ? (
                  <span className={styles.radioDotInner} />
                ) : null}
              </span>
              Group ACH
            </button>
            <button
              className={styles.radioBtn}
              type="button"
              onClick={() => setManualField('source', ManualRefundSource.Check)}
            >
              <span
                className={`${styles.radioDot}${
                  manualForm.source === ManualRefundSource.Check
                    ? ` ${styles.active}`
                    : ''
                }`}
              >
                {manualForm.source === ManualRefundSource.Check ? (
                  <span className={styles.radioDotInner} />
                ) : null}
              </span>
              Check
            </button>
          </div>
        </div>
      ) : null}

      {manualForm.type === 'refund' &&
      manualForm.source === ManualRefundSource.Check ? (
        <div>
          <div className={styles.fieldLabel}>
            Check number<span className={styles.req}> *</span>
          </div>
          <input
            className={styles.formInput}
            type="text"
            inputMode="numeric"
            aria-label="Check number"
            placeholder="e.g. 1042"
            maxLength={20}
            value={manualForm.checkNumber}
            onChange={(e) => setManualField('checkNumber', e.target.value)}
          />
          {manualForm.checkNumber.trim() !== '' &&
          !isValidCheckNumber(manualForm.checkNumber) ? (
            <div className={styles.fieldError}>
              Check number must be digits only (up to 20).
            </div>
          ) : null}
        </div>
      ) : null}

      {manualForm.type === 'refund' ? (
        <div>
          <div className={styles.fieldLabel}>
            Refund reason<span className={styles.req}> *</span>
          </div>
          <Select
            value={manualForm.reason}
            onChange={(v: RefundReason) => setManualField('reason', v)}
            style={{ width: '100%' }}
            options={REFUND_REASONS.map((r) => ({
              value: r,
              label: REFUND_REASON_LABELS[r],
            }))}
          />
        </div>
      ) : null}

      <div>
        <div className={styles.fieldLabel}>
          Date<span className={styles.req}> *</span>
        </div>
        <input
          className={styles.formInput}
          type="date"
          value={manualForm.date}
          onChange={(e) => setManualField('date', e.target.value)}
        />
      </div>

      <div>
        <div className={styles.fieldLabel}>
          Select benefit<span className={styles.req}> *</span>
        </div>
        <BenefitSelect
          value={manualForm.benefit}
          onChange={(b) =>
            // Switching benefits invalidates the picked month.
            setManualForm((f) => ({ ...f, benefit: b, month: null }))
          }
          year={scopeYear}
          placeholder="Search by name or benefit ID…"
          disabled={!canWrite}
        />
      </div>

      {manualForm.benefit ? (
        <div>
          <div className={styles.fieldLabel}>
            Select month<span className={styles.req}> *</span>
          </div>
          {isManualSuggestError ? (
            <Alert
              type="error"
              message="Could not load period suggestions"
              description="Failed to fetch available months. Please try again."
              style={{ marginTop: 4 }}
            />
          ) : (manualSuggestions?.length ?? 0) > 0 ? (
            <MonthChipGrid
              suggestions={manualSuggestions ?? []}
              isSelected={(period) => manualForm.month === period}
              onToggle={(period) =>
                setManualField(
                  'month',
                  manualForm.month === period ? null : period
                )
              }
            />
          ) : null}
        </div>
      ) : null}

      <div>
        <div className={styles.fieldLabel}>
          Note <span className={styles.optionalMark}>(optional)</span>
        </div>
        <textarea
          className={styles.noteTextarea}
          value={manualForm.note}
          placeholder="Add a note…"
          rows={3}
          disabled={!canWrite}
          onChange={(e) => setManualField('note', e.target.value)}
        />
      </div>
    </div>
  )

  // Set Expected Price accordion body — cell mode only (the section is
  // filtered out of the accordion otherwise). Title comes from the accordion
  // header, so only the form renders here.
  const setExpectedBody = (
    <>
      <div className={styles.formRow}>
        <div className={styles.formMoneyWrap}>
          <div className={styles.fieldLabel}>
            Amount<span className={styles.req}> *</span>
          </div>
          <input
            className={`${styles.formInput} ${styles.moneyInput}`}
            value={setExpectedAmount}
            inputMode="decimal"
            placeholder="0.00"
            onChange={(e) => {
              const raw = e.target.value
              if (isMoneyInput(raw)) setSetExpectedAmount(raw)
            }}
          />
        </div>
        <div style={{ flex: 2, minWidth: 180 }}>
          <div className={styles.fieldLabel}>
            Note <span className={styles.optionalMark}>(optional)</span>
          </div>
          <input
            className={styles.formInput}
            value={setExpectedNote}
            placeholder="Add a note…"
            disabled={!canWrite}
            onChange={(e) => setSetExpectedNote(e.target.value)}
          />
        </div>
        <div className={styles.formActions}>
          <button
            className={`${styles.formSubmitBtn}${
              setExpectedPrice.isPending ? ` ${styles.pending}` : ''
            }`}
            type="button"
            disabled={
              !setExpectedValid || setExpectedPrice.isPending || !canWrite
            }
            onClick={handleSetExpected}
          >
            {setExpectedPrice.isPending ? 'Setting…' : 'Set expected'}
          </button>
        </div>
      </div>
      {setExpectedPrice.isError ? (
        <Alert
          type="error"
          message={`Failed to set expected price: ${extractApiErrorMessage(setExpectedPrice.error)}`}
          style={{ marginTop: space.md }}
        />
      ) : null}
    </>
  )

  // Textarea A — the justification for an "Absorb" accordion write-off, shared
  // by both of that section's modes. Required, not optional: the server keeps no
  // prior note, so this field is the only thing standing between a re-allocate
  // and a lost justification (ADR 0003).
  const absorbNoteValid = isAbsorptionNoteValid(absorbNote)
  const absorbNoteField = (
    <div style={{ marginBottom: space.md }}>
      <div className={styles.fieldLabel}>
        Reason for write-off<span className={styles.req}> *</span>
      </div>
      <textarea
        className={styles.noteTextarea}
        value={absorbNote}
        placeholder="Why is this being written off?"
        rows={3}
        maxLength={ABSORPTION_NOTE_MAX_LENGTH}
        disabled={!canWrite}
        onChange={(e) => setAbsorbNote(e.target.value)}
      />
    </div>
  )

  // "Absorb" accordion body — two modes, no free-entry amount:
  //  • Overpaid cell → Partial write-off of the excess (paid − expected); the
  //    allocation is trimmed to expected and the difference absorbed.
  //  • Otherwise (unallocated tx) → Full write-off of the whole transaction.
  const absorbBody = isOverpaidCell ? (
    <>
      <Alert
        type="warning"
        showIcon
        message={`Absorb the ${fmtMoney(overpayExcess)} overpayment (paid ${fmtMoney(monthTotal)} vs expected ${fmtMoney(cellExpected ?? 0)}). The allocation is reduced to expected and the excess is written off as a Partial absorption; the period stays reconciled.`}
        style={{ marginBottom: space.md }}
      />
      {absorbNoteField}
      <div className={styles.footerActions}>
        <button
          className={styles.primaryBtn}
          type="button"
          disabled={
            overpayExcess <= 0 ||
            !absorbNoteValid ||
            allocateBatch.isPending ||
            !canWrite
          }
          onClick={handleAbsorbOverpayment}
        >
          {allocateBatch.isPending
            ? 'Absorbing…'
            : `Absorb ${fmtMoney(overpayExcess)}`}
        </button>
      </div>
    </>
  ) : (
    <>
      <Alert
        type="warning"
        showIcon
        message={`This records the entire ${fmtMoney(absorbRemainder)} transaction as a company-absorbed write-off (Full). The transaction is marked Absorbed and kept out of allocation.`}
        style={{ marginBottom: space.md }}
      />
      {absorbNoteField}
      <div className={styles.footerActions}>
        <button
          className={styles.primaryBtn}
          type="button"
          disabled={
            absorbRemainder <= 0 ||
            !absorbNoteValid ||
            recordAbsorption.isPending ||
            !canWrite
          }
          onClick={handleConfirmAbsorptionAction}
        >
          {recordAbsorption.isPending
            ? 'Absorbing…'
            : `Absorb ${fmtMoney(absorbRemainder)}`}
        </button>
      </div>
    </>
  )

  const manualPending = eePaid.isPending || manualRefund.isPending
  const manualFooter = (
    <div className={styles.footerActions}>
      <button className={styles.cancelBtn} type="button" onClick={onClose}>
        Cancel
      </button>
      <button
        className={styles.primaryBtn}
        type="button"
        disabled={!manualValid || manualPending || !canWrite}
        onClick={() => handleManualSubmit()}
      >
        {manualPending ? 'Adding…' : 'Add transaction'}
      </button>
    </div>
  )

  const allocateRemainBar = showFullEquation ? (
    <div className={styles.equationBar}>
      <div className={styles.equationTerms}>
        <div className={styles.term}>
          <span className={styles.termKicker}>(Source)</span>
          <span className={styles.termAmount}>{bareFmt(sourceAmount)}</span>
        </div>
        {equationTerms.map((t) => (
          <div className={styles.equationTerms} key={t.id}>
            <span className={styles.minus}>−</span>
            <div className={styles.term}>
              <span className={styles.termKicker}>({t.label})</span>
              <span className={styles.termAmount}>{bareFmt(t.total)}</span>
            </div>
          </div>
        ))}
      </div>
      <div className={styles.remainWrap}>
        <span className={styles.termKicker}>Remain amount</span>
        <span className={`${styles.remainAmount} ${styles[remainderState]}`}>
          = {remainder < 0 ? '−' : ''}
          {bareFmt(remainder)}
        </span>
        {!isFullyAllocated ? (
          <span className={`${styles.remainHint} ${styles[remainderState]}`}>
            {overAllocated
              ? 'Over the source amount'
              : 'Allocate the full amount to apply'}
          </span>
        ) : null}
      </div>
    </div>
  ) : (
    <div className={styles.simpleRemainBar}>
      <span className={styles.simpleRemainLabel}>Remaining to allocate</span>
      <span className={`${styles.remainAmount} ${styles[remainderState]}`}>
        {remainder < 0 ? '−' : ''}
        {fmtMoney(Math.abs(remainder))}
      </span>
    </div>
  )

  // "Add payment" accordion body — the manual form plus its own actions.
  // Cancel collapses back to Allocate when it's available; when it isn't (empty
  // cell, `sourceReady` false), collapsing to 'allocate' would fall straight
  // back to Add payment via `effectiveSection`, making Cancel a no-op — so
  // collapse the accordion entirely (null) instead, which always dismisses the
  // form.
  const addPaymentBody = (
    <>
      {manualBody}
      <div className={styles.footerActions} style={{ marginTop: space.lg }}>
        <button
          className={styles.cancelBtn}
          type="button"
          onClick={() => setActiveSection(sourceReady ? 'allocate' : null)}
        >
          Cancel
        </button>
        <button
          className={styles.primaryBtn}
          type="button"
          disabled={!manualValid || manualPending || !canWrite}
          onClick={() =>
            handleManualSubmit(() => setManualForm(emptyManualForm()))
          }
        >
          {manualPending ? 'Adding…' : 'Add payment'}
        </button>
      </div>
    </>
  )

  // "Allocate payment" accordion body — refund-reason picker, allocation
  // groups, note, and the remain bar + Cancel/Apply actions.
  const allocateBody = (
    <>
      {/* Refund-reason picker — only shown when this is a refund
      (negative-amount) source. Defaults to REFUND_OTHER. */}
      {sourceSign < 0 ? (
        <div style={{ marginBottom: space.md }}>
          <div className={styles.fieldLabel}>Refund reason</div>
          <Select
            value={refundReason}
            onChange={(v: RefundReason) => setRefundReason(v)}
            style={{ width: '100%' }}
            options={REFUND_REASONS.map((r) => ({
              value: r,
              label: REFUND_REASON_LABELS[r],
            }))}
          />
        </div>
      ) : null}

      <div className={styles.groups}>
        {groups.map((g, i) => {
          // Exclude benefits picked by OTHER groups so operators can't
          // split the same benefit twice (which is meaningless — the
          // two rows would just collapse into one). The group's own
          // benefit is intentionally kept visible by BenefitSelect.
          const excludeBenefitIds = new Set(
            groups
              .filter((other) => other.id !== g.id && !!other.benefit)
              .map((other) => other.benefit!.benefitId)
          )
          return (
            <AllocationGroup
              key={g.id}
              group={g}
              sourceAmount={sourceAmount}
              transactionDate={
                unassigned
                  ? undefined
                  : (activeTx?.transactionDate ?? undefined)
              }
              year={scopeYear}
              currentBenefit={sourceBenefitOption}
              open={open}
              onBenefitChange={handleBenefitChange}
              onToggleMonth={handleToggleMonth}
              onRowAmount={handleRowAmount}
              onRemoveRow={handleRemoveRow}
              onSplitEvenly={handleSplitEvenly}
              onRemoveGroup={handleRemoveGroup}
              divided={i > 0}
              excludeBenefitIds={excludeBenefitIds}
              canWrite={canWrite}
            />
          )
        })}
        <button
          className={styles.addGroupBtn}
          type="button"
          disabled={!canWrite}
          onClick={handleAddGroup}
        >
          <PlusIcon />
          Split to another benefit
        </button>
      </div>

      <div style={{ marginTop: space.md }}>
        <div className={styles.fieldLabel}>
          Note <span className={styles.optionalMark}>(optional)</span>
        </div>
        <textarea
          className={styles.noteTextarea}
          value={adjustNote}
          placeholder="Add a note…"
          rows={3}
          disabled={!canWrite}
          onChange={(e) => setAdjustNote(e.target.value)}
        />
      </div>

      <div className={styles.allocateActionsBar}>
        {allocateRemainBar}
        <div className={styles.footerActions}>
          <button className={styles.cancelBtn} type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.primaryBtn}
            type="button"
            disabled={!canApply || allocateBatch.isPending || !canWrite}
            onClick={handleApply}
          >
            {allocateBatch.isPending ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </>
  )

  const returnNoteTrimmed = returnNote.trim()
  const handleReturn = async () => {
    if (!unassigned || returnNoteTrimmed.length === 0) return
    try {
      await returnTransaction.mutateAsync({
        transactionId: unassigned.transactionId,
        note: returnNoteTrimmed,
      })
      message.success('Payment returned')
      onClose()
    } catch {
      /* onError shows the toast; keep the modal open so the note survives */
    }
  }

  // "Return" accordion body — iStream only. The returned payment leaves
  // Unallocated payments, is never auto-allocated, and is excluded from the
  // ledger totals; it can be un-returned from Transaction Details.
  const returnBody = (
    <>
      <Alert
        type="warning"
        showIcon
        message={`This returns the ${fmtMoney(sourceAmount)} iStream payment. It is removed from Unallocated payments, will not be auto-allocated, and is excluded from Transaction Details totals. You can un-return it later from Transaction Details.`}
        style={{ marginBottom: space.md }}
      />
      <div style={{ marginBottom: space.md }}>
        <div className={styles.fieldLabel}>
          Return note<span className={styles.req}> *</span>
        </div>
        <textarea
          className={styles.noteTextarea}
          value={returnNote}
          placeholder="Why is this payment being returned?"
          rows={3}
          maxLength={RETURN_NOTE_MAX_LENGTH}
          disabled={!canWrite}
          onChange={(e) => setReturnNote(e.target.value)}
        />
      </div>
      <div className={styles.footerActions}>
        <button
          className={styles.primaryBtn}
          type="button"
          disabled={
            returnNoteTrimmed.length === 0 ||
            returnTransaction.isPending ||
            !canWrite
          }
          onClick={() => void handleReturn()}
        >
          {returnTransaction.isPending ? 'Submitting…' : 'Submit'}
        </button>
      </div>
    </>
  )

  const sectionDefs: Record<
    AdjustSectionKey,
    { title: string; body: React.ReactNode }
  > = {
    allocate: {
      title: 'Allocate payment',
      body: allocateBody,
    },
    addPayment: {
      title: 'Add payment',
      body: addPaymentBody,
    },
    setExpected: {
      title: 'Set expected price',
      body: setExpectedBody,
    },
    absorb: {
      title: 'Absorb payment',
      body: absorbBody,
    },
    explicitCredit: {
      title: 'Explicit credit',
      body: (
        <ExplicitCreditSection
          credits={credits}
          form={explicitCreditForm}
          hasLiveAllocations={hasLiveAllocations}
          canWrite={canWrite}
        />
      ),
    },
    return: {
      title: 'Return payment',
      body: returnBody,
    },
  }

  const handleSectionToggle = (key: AdjustSectionKey) => {
    // Clicking the open header collapses it; clicking another opens that one
    // (single-open accordion).
    if (effectiveSection === key) {
      setActiveSection(null)
      return
    }
    if (key === 'addPayment') {
      // Prefill from the current adjust context (benefit + month) — same
      // behavior as the old inline Add-payment affordance.
      setManualForm(() => ({
        ...emptyManualForm(),
        benefit:
          cellBenefitId && !benefitUnresolved
            ? { benefitId: cellBenefitId, label: insuredLabel }
            : null,
        month: cellPeriod ?? null,
        date:
          unassigned?.date && dayjs(unassigned.date).isValid()
            ? dayjs(unassigned.date).format('YYYY-MM-DD')
            : emptyManualForm().date,
      }))
    }
    setActiveSection(key)
  }

  const accordion = (
    <div className={styles.accordion}>
      {availableSections.map((key) => {
        const def = sectionDefs[key]
        const isOpen = effectiveSection === key
        // Add-payment and set-expected are pure write forms — lock their headers
        // for read-only users. Explicit credit is NOT locked: its list of
        // credits is read value, and its entry row / remove buttons are
        // individually disabled (same treatment as allocate/absorb).
        const sectionDisabled =
          !canWrite && (key === 'addPayment' || key === 'setExpected')
        return (
          <div className={styles.accordionItem} key={key}>
            <button
              className={styles.accordionHeader}
              type="button"
              aria-expanded={isOpen}
              disabled={sectionDisabled}
              onClick={() => handleSectionToggle(key)}
            >
              <span className={styles.accordionHeaderTitle}>{def.title}</span>
              <span
                className={`${styles.chevron}${isOpen ? ` ${styles.open}` : ''}`}
              >
                <ChevronDownIcon />
              </span>
            </button>
            {isOpen ? (
              <div className={styles.accordionBody}>{def.body}</div>
            ) : null}
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        centered
        width={ADJUST_MODAL_WIDTH}
        destroyOnClose
        title={
          <div className={styles.headerTitle}>
            {isManual ? 'Add Payment' : 'Adjust Payment'}
          </div>
        }
        styles={{
          mask: { background: 'rgba(17, 42, 67, 0.55)' },
          header: {
            borderBottom: `1px solid ${colors.border.subtle}`,
            paddingBottom: 14,
            marginBottom: 0,
          },
          body: { maxHeight: 'calc(85vh - 220px)', overflowY: 'auto' },
          footer: {
            borderTop: `1px solid ${colors.border.subtle}`,
            paddingTop: 14,
            marginTop: 0,
          },
        }}
        footer={isManual ? manualFooter : null}
      >
        <div className={styles.panelBody}>
          {isManual ? (
            manualBody
          ) : !unassigned && isLoading ? (
            <Spin />
          ) : !unassigned && isPeriodDetailError ? (
            <Alert
              type="error"
              message="Failed to load period detail"
              description="Could not retrieve payment data for this benefit-month. Please close and try again."
              style={{ marginTop: 8 }}
            />
          ) : (
            <div className={styles.twoCol}>
              <div className={styles.leftCol}>
                {!sourceReady && !moneySummary.hasCredit ? (
                  <div className={styles.railEmpty}>
                    No payment allocated to this benefit-month yet — nothing to
                    adjust.
                  </div>
                ) : (
                  <>
                    <div className={styles.railTotals}>
                      <div className={styles.railTotalRow}>
                        <span className={styles.railTotalLabel}>Total:</span>
                        <span
                          className={`${styles.railTotalAmount}${
                            flagged ? ` ${styles.flagged}` : ''
                          }`}
                        >
                          {unassigned
                            ? fmtMoney(
                                sourceSign < 0 ? -sourceAmount : sourceAmount
                              )
                            : fmtMoney(monthTotal)}
                        </span>
                      </div>
                      {!unassigned && detail?.expectedAmount != null ? (
                        <div className={styles.railExpectedRow}>
                          <span className={styles.railExpectedLabel}>
                            Expected:
                          </span>
                          <span className={styles.railExpectedAmount}>
                            {fmtMoney(detail.expectedAmount)}
                          </span>
                        </div>
                      ) : null}
                      {/* Credit / net-due lines only appear on a credited
                          benefit-month — an uncredited cell reads exactly as
                          it did before this feature. */}
                      {!unassigned && moneySummary.hasCredit ? (
                        <>
                          <div className={styles.railExpectedRow}>
                            {/* The count appears only once there is more than
                                one row behind the figure, so the operator knows
                                the number is a sum before scrolling to the list
                                below. A single credit needs no arithmetic
                                explained. */}
                            <span className={styles.railExpectedLabel}>
                              Explicit credit
                              {moneySummary.creditCount > 1
                                ? ` (${moneySummary.creditCount})`
                                : ''}
                              :
                            </span>
                            <span className={styles.railCreditAmount}>
                              −{fmtMoney(moneySummary.credited)}
                            </span>
                          </div>
                          {/* Separate guard: a period can carry credit rows
                              while no expected resolves, and "Net due $0.00"
                              would be a lie rather than a blank. */}
                          {moneySummary.netDue != null ? (
                            <div className={styles.railExpectedRow}>
                              <span className={styles.railNetDueLabel}>
                                Net due:
                              </span>
                              <span className={styles.railNetDueAmount}>
                                {fmtMoney(moneySummary.netDue)}
                              </span>
                            </div>
                          ) : null}
                          {/* Load-bearing, not decorative: a rebate often
                              explains only part of a gap, and this is where the
                              operator decides whether the remainder is a wrong
                              expected price or genuinely unpaid money. */}
                          {/* Suppressed with no live allocation: decideIssue
                              clears the issue when a period has none, so the
                              period has deliberately gone quiet on the server
                              and "$500.00 short" here would contradict a cell
                              the strip renders as clean. The debt survives and
                              is still printed above — it is the SHORTFALL, an
                              issue-shaped claim, that would be the lie. */}
                          {moneySummary.shortfall > 0 && sourceReady ? (
                            <div className={styles.railShortfallRow}>
                              <span className={styles.railShortfall}>
                                ({fmtMoney(moneySummary.shortfall)} short)
                              </span>
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>

                    <div className={styles.railDivider} />

                    {unassigned ? (
                      <div className={styles.railContext}>
                        <span className={styles.sourceKicker}>
                          Unassigned transaction
                        </span>
                        <span className={styles.railContextLine}>
                          {`${unassigned.carrier ? `${unassigned.carrier} · ` : ''}${unassigned.date}`}
                        </span>
                        {txLabels ? (
                          <span className={styles.railContextLine}>
                            {txLabels.line}
                          </span>
                        ) : null}
                        {sourceType && !txLabels?.type ? (
                          <span
                            className={`${styles.typeBadge} ${
                              sourceType === 'refund'
                                ? styles.refund
                                : styles.eePaid
                            }`}
                          >
                            {sourceType === 'refund' ? 'Refund' : 'EE Paid'}
                          </span>
                        ) : null}
                        {unassigned.transactionNumber ? (
                          <div className={styles.railIdRow}>
                            <span className={styles.railId}>
                              Transaction number: {unassigned.transactionNumber}
                            </span>
                            <button
                              className={styles.copyBtn}
                              type="button"
                              aria-label="Copy transaction number"
                              onClick={() =>
                                copyTransactionNumber(
                                  unassigned.transactionNumber
                                )
                              }
                            >
                              <CopyIcon />
                            </button>
                          </div>
                        ) : sourceConf ? (
                          <div className={styles.railIdRow}>
                            <span className={styles.railId}>
                              TXN: {getShortText(sourceConf, 6)}
                            </span>
                            <button
                              className={styles.copyBtn}
                              type="button"
                              aria-label="Copy confirmation code"
                              onClick={handleCopyConf}
                            >
                              <CopyIcon />
                            </button>
                          </div>
                        ) : null}
                        {canDeleteUnassigned ? (
                          <button
                            className={styles.deletePaymentBtn}
                            type="button"
                            title="Permanently delete this manually added payment"
                            disabled={
                              deleteManualPayment.isPending || !canWrite
                            }
                            onClick={handleDeleteUnassigned}
                          >
                            <TrashIcon />
                            Delete payment
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <div className={styles.railContext}>
                        <span className={styles.railContextLine}>
                          {cellTarget
                            ? dayjs(cellTarget.periodStartDate).format(
                                'MMMM YYYY'
                              )
                            : ''}
                        </span>
                        <span className={styles.railContextLine}>
                          {insuredLabel}
                        </span>
                        {/* Hidden for an unresolved benefit: the id leads
                            nowhere — it cannot be searched, opened or re-picked
                            — and beside "Unresolved benefit" it reads as the
                            identity of a benefit the operator could go look up.
                            The ledger row still carries it for anyone who needs
                            to chase it in the DB. */}
                        {cellTarget && !benefitUnresolved ? (
                          <div className={styles.railIdRow}>
                            <span className={styles.railId}>
                              Benefit ID:{' '}
                              {getShortText(cellTarget.benefitId, 4)}
                            </span>
                            <button
                              className={styles.copyBtn}
                              type="button"
                              aria-label="Copy benefit ID"
                              onClick={() => {
                                void navigator.clipboard.writeText(
                                  cellTarget.benefitId
                                )
                                message.success('Copied benefit ID')
                              }}
                            >
                              <CopyIcon />
                            </button>
                            {/* The benefit id's own destination, beside the
                                button that copies it — the drawer's half of the
                                control the Benefits tab carries in its Insured
                                cell. Both go through `getApplicationDetailsPath`
                                so the placements cannot drift.

                                An anchor, not a button like the Transaction Details jump-out
                                above it: that one runs `openInTransactionDetails`
                                in this tab, while this leaves the app entirely.
                                A real href is what makes middle-click and
                                "open in new tab" work, and `target="_blank"`
                                keeps the operator's place in this drawer —
                                which holds unsaved allocation edits.

                                The cell's benefit id is the whole address, so
                                the drawer needs no date: the coverage month it
                                is looking at does not narrow an application
                                opened by its own id.

                                Not gated on `canWrite`, unlike the buttons
                                around it: opening a page is a read, and the
                                destination enforces its own roles. */}
                            <a
                              className={`${styles.copyBtn} ${styles.railIdLink}`}
                              href={getApplicationDetailsPath(
                                cellTarget.benefitId
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={OPEN_IN_APP_ENGINE_LABEL}
                              aria-label={OPEN_IN_APP_ENGINE_LABEL}
                            >
                              <ExternalLinkIcon />
                            </a>
                          </div>
                        ) : null}
                      </div>
                    )}

                    {transactions.length >= 1 ? (
                      <div className={styles.railCards}>
                        {transactions.map((tx) => {
                          const isSelected =
                            tx.transactionId === activeTx?.transactionId
                          return (
                            <div
                              className={`${styles.txnRow}${
                                isSelected ? ` ${styles.selected}` : ''
                              }`}
                              key={tx.transactionId}
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedTxId(tx.transactionId)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setSelectedTxId(tx.transactionId)
                                }
                              }}
                            >
                              <div className={styles.txnCardTop}>
                                <span className={styles.txnCardAmount}>
                                  {fmtMoney(tx.amountInThisPeriod)}
                                  {isSplitTx(tx) ? <SplitIcon /> : null}
                                </span>
                                {tx.source ? (
                                  <span className={styles.txnCardPill}>
                                    {SOURCE_LABEL[tx.source] ?? tx.source}
                                  </span>
                                ) : null}
                              </div>
                              {tx.transactionNumber ? (
                                <span className={styles.txnCardNumber}>
                                  {tx.transactionNumber}
                                </span>
                              ) : null}
                              <div className={styles.txnCardBottom}>
                                <span className={styles.txnCardDate}>
                                  {tx.transactionDate
                                    ? dayjs(tx.transactionDate).format(
                                        'MMM D, YYYY'
                                      )
                                    : '—'}
                                </span>
                                {/* Row-end actions on the selected tx. Remove
                              (un-allocate) shows for every tx; Delete (trash)
                              only for manually-added txns — rail-sourced
                              iStream / Marqeta / WEX payments can't be deleted. */}
                                <div className={styles.txnCardActions}>
                                  {isSelected && canRemoveAllocation ? (
                                    <button
                                      className={styles.removeAllocBtn}
                                      type="button"
                                      title="Remove allocation — returns the transaction to the review queue"
                                      aria-label="Remove allocation"
                                      disabled={
                                        deallocate.isPending || !canWrite
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleRemoveAllocation()
                                      }}
                                    >
                                      <UnlinkIcon />
                                    </button>
                                  ) : null}
                                  {isSelected && canDeleteTxn ? (
                                    <button
                                      className={styles.deleteTxnBtn}
                                      type="button"
                                      title="Delete transaction"
                                      aria-label="Delete transaction"
                                      disabled={
                                        deallocate.isPending || !canWrite
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDeleteTxn()
                                      }}
                                    >
                                      <TrashIcon />
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : null}

                    <div className={styles.railSpacer} />

                    {cellTarget ? (
                      <div className={styles.flagRow}>
                        <button
                          className={`${styles.flagBtn}${
                            flagged ? ` ${styles.active}` : ''
                          }`}
                          type="button"
                          disabled={isFlagMutating || !canWrite}
                          onClick={handleToggleFlag}
                        >
                          {isFlagMutating ? (
                            <Spin size="small" />
                          ) : (
                            <FlagIcon filled={flagged} />
                          )}
                          {flagged ? 'Un-flag' : 'Flag as issue'}
                        </button>
                      </div>
                    ) : null}
                    {cellTarget &&
                    (flagIssue.isError || unflagIssue.isError) ? (
                      <Alert
                        type="error"
                        message={
                          flagIssue.isError
                            ? `Failed to flag issue: ${extractApiErrorMessage(flagIssue.error)}`
                            : `Failed to unflag issue: ${extractApiErrorMessage(unflagIssue.error)}`
                        }
                        style={{ marginTop: space.sm }}
                      />
                    ) : null}
                  </>
                )}
              </div>
              <div className={styles.rightCol}>
                {sourceReady && activeTx ? (
                  <div className={styles.detailHeader}>
                    <div className={styles.detailHeaderLeft}>
                      <div className={styles.detailDateRow}>
                        <span className={styles.detailDateLabel}>
                          Transaction date:
                        </span>
                        <span className={styles.detailDateValue}>
                          {activeTx.transactionDate
                            ? dayjs(activeTx.transactionDate).format(
                                'MMM D, YYYY'
                              )
                            : '—'}
                        </span>
                      </div>
                      <div className={styles.railIdRow}>
                        <span className={styles.railId}>
                          Transaction number:{' '}
                          {activeTx.transactionNumber || '—'}
                        </span>
                        {activeTx.transactionNumber ? (
                          <button
                            className={styles.copyBtn}
                            type="button"
                            aria-label="Copy transaction number"
                            onClick={() =>
                              copyTransactionNumber(activeTx.transactionNumber)
                            }
                          >
                            <CopyIcon />
                          </button>
                        ) : null}
                        <button
                          className={styles.copyBtn}
                          type="button"
                          aria-label="Open this transaction in Transaction Details"
                          title="Open in Transaction Details"
                          onClick={() =>
                            openInTransactionDetails(
                              activeTx.transactionId,
                              activeTx.transactionDate
                            )
                          }
                        >
                          <ExternalLinkIcon />
                        </button>
                      </div>
                    </div>
                    <div className={styles.detailHeaderRight}>
                      <span className={styles.detailAmount}>
                        {isSplitTx(activeTx) ? <SplitIcon /> : null}
                        {fmtMoney(activeTx.amountInThisPeriod)}
                      </span>
                      <span className={styles.detailFromTotal}>
                        From total: {fmtMoney(activeTx.fullAmount)}
                      </span>
                    </div>
                  </div>
                ) : null}
                {/* The benefit's year, read-only, above the sections — the
                    context an operator needs BEFORE choosing one of them. */}
                <BenefitYearStrip
                  benefitId={stripBenefitId}
                  amount={stripAmount}
                  // Mirrors what <AllocationGroup> passes, exactly. Two reasons
                  // it is not `?? unassigned.date`: that field is a DISPLAY
                  // string ("Jun 3, 2026"), not a date the API can parse; and
                  // the allocate grid sends `undefined` in unassigned mode, so
                  // anything else here splits the React Query key and costs the
                  // second request this strip exists to avoid.
                  transactionDate={
                    unassigned
                      ? undefined
                      : (activeTx?.transactionDate ?? undefined)
                  }
                />
                {accordion}
              </div>
            </div>
          )}
        </div>
      </Modal>
      {absorptionPrompt && (
        <Modal
          open
          centered
          title="Write off the remainder?"
          okText={`Allocate & write off ${fmtMoney(absorptionPrompt.remainder)}`}
          okButtonProps={{
            disabled: !isAbsorptionNoteValid(absorptionPrompt.note),
          }}
          confirmLoading={allocateBatch.isPending}
          onOk={handleConfirmAbsorption}
          onCancel={() => setAbsorptionPrompt(null)}
        >
          <Alert
            type="warning"
            showIcon
            message={`${fmtMoney(absorptionPrompt.remainder)} of this ${fmtMoney(
              sourceAmount
            )} transaction will be recorded as an absorption (a company-absorbed write-off) and kept out of the allocation. The period stays reconciled.`}
            style={{ marginBottom: space.md }}
          />
          {/* Textarea B — this prompt's own justification. Required for the same
              reason as textarea A: the note is re-recorded, never carried
              forward, so it has to be restated here. Lives on `absorptionPrompt`
              so Cancel discards it with the rest of the pending batch. */}
          <div className={styles.fieldLabel}>
            Reason for write-off<span className={styles.req}> *</span>
          </div>
          <textarea
            className={styles.noteTextarea}
            value={absorptionPrompt.note}
            placeholder="Why is this remainder being written off?"
            rows={3}
            maxLength={ABSORPTION_NOTE_MAX_LENGTH}
            onChange={(e) => {
              const { value } = e.target
              setAbsorptionPrompt((prev) =>
                prev ? { ...prev, note: value } : prev
              )
            }}
          />
        </Modal>
      )}
    </>
  )
}

export default AdjustPaymentModal
